// [ai] The shell's own state: the sidebar (a rail beside the page from 1100 px, a drawer below), the grip's width, the bar's
// measured height, the server-events connection and the breadcrumb. One store, created once in main.tsx (tests make their
// own); components read it through useShell(). Persistence keeps the keys the old shell wrote, so Isaac's preferences carry over:
// `atlas-side` ("0" = the rail was closed once — only a rail is ever remembered), `atlas-side-w` (written on release, never mid-drag).
import { createStore, useStore, type StoreApi } from "zustand";
import { createContext, useContext } from "react";

export const RAIL_PX = 1100;   // tokens.css documents the same scale (media queries cannot read custom properties)
export const SIDE = { default: 240, min: 192, max: 288, collapse: 120 } as const;   // the grip: ±20 % of 240, far left collapses
export const KEYS = { open: "atlas-side", width: "atlas-side-w", folds: "atlas-tree-closed", scroll: "atlas-side-y" } as const;

export type SideMode = "rail" | "drawer";
export type ShellState = {
  side: { mode: SideMode; open: boolean; w: number; dragging: boolean };
  barH: number;
  sse: { connected: boolean; events: number };
  crumb: string;
  title: string;   // what the page says it is (a document's title); the crumb when the tree has no entry
  dockB: number;   // px the Astra dock takes at the bottom (--at-dock-b on the shell root: the page, the drop and the toasts keep clear of it)
  setSide: (open: boolean) => void;
  toggleSide: () => void;
  syncViewport: () => void;
  dragWidth: (px: number) => void;
  endDrag: () => void;
  setBarH: (px: number) => void;
  setSse: (patch: { connected?: boolean; event?: true }) => void;
  setCrumb: (crumb: string) => void;
  setTitle: (title: string) => void;
  setDockB: (px: number) => void;
};

export const isRail = () => typeof matchMedia === "function" && matchMedia(`(min-width: ${RAIL_PX}px)`).matches;
const read = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private mode: the session still works */ } };
const clamp = (n: number) => Math.min(SIDE.max, Math.max(SIDE.min, n));
const railPref = () => read(KEYS.open) !== "0";   // open unless closed once
const storedWidth = () => { const n = Number(read(KEYS.width)); return Number.isFinite(n) && n > 0 ? clamp(n) : SIDE.default; };

export function createShellStore(): StoreApi<ShellState> {
  return createStore<ShellState>((set, get) => ({
    side: { mode: isRail() ? "rail" : "drawer", open: isRail() && railPref(), w: storedWidth(), dragging: false },
    barH: 53, sse: { connected: false, events: 0 }, crumb: "", title: "", dockB: 0,
    setSide: open => { const side = get().side; set({ side: { ...side, open } }); if (side.mode === "rail") write(KEYS.open, open ? "1" : "0"); },
    toggleSide: () => get().setSide(!get().side.open),
    syncViewport: () => { const mode: SideMode = isRail() ? "rail" : "drawer"; set({ side: { ...get().side, mode, open: mode === "rail" && railPref(), dragging: false } }); },
    dragWidth: px => {
      const side = get().side;
      if (px < SIDE.collapse) { set({ side: { ...side, w: SIDE.default, open: false, dragging: false } }); write(KEYS.width, String(SIDE.default)); if (side.mode === "rail") write(KEYS.open, "0"); return; }
      set({ side: { ...side, w: clamp(px), dragging: true } });
    },
    endDrag: () => { const side = get().side; if (!side.dragging) return; set({ side: { ...side, dragging: false } }); write(KEYS.width, String(side.w)); },
    setBarH: px => set({ barH: px }),
    setSse: p => set(s => ({ sse: { connected: p.connected ?? s.sse.connected, events: s.sse.events + (p.event ? 1 : 0) } })),
    setCrumb: crumb => set({ crumb }),
    setTitle: title => set({ title }),
    setDockB: px => set(s => (s.dockB === px ? s : { dockB: px })),
  }));
}

export const ShellStoreContext = createContext<StoreApi<ShellState> | null>(null);
export function useShellStore(): StoreApi<ShellState> {
  const s = useContext(ShellStoreContext);
  if (!s) throw new Error("useShell outside <App>: the shell store is provided by App");
  return s;
}
export function useShell<T>(selector: (s: ShellState) => T): T { return useStore(useShellStore(), selector); }
